import React, { ReactNode, useContext, useEffect, useState } from 'react';
import Spinner from '../../Spinner';
import { GlobalErrorContext } from '../../../contexts/GlobalError';
import classNames from 'classnames';

import { ReactComponent as Error } from 'icons/error.svg';
import http from '../../Profile/http';
import { usePrescriptionContext } from './PrescriptioWasSentContext';

type SendPaymentLinkButtonProps = {
  handleRefetchAppointments: () => void;
  appointmentId: number;
  wasPaymentLinkSent?: boolean;
};

type State = {
  buttonContent: ReactNode;
  buttonClass: string;
  helpText: string | undefined;
  disabled: boolean;
};

type StateDictionary = {
  generalError: State;
  idle: State;
  paymentLinkSent: State;
  loading: State;
  prescriptionNotSent: State;
};

const stateMap: StateDictionary = {
  idle: {
    buttonContent: 'Zahlungslink senden',
    buttonClass: 'text-white bg-swopa-secondary-light-blue hover:bg-[#2B81EC]',
    helpText: undefined,
    disabled: false,
  },
  loading: {
    buttonContent: <Spinner fill="#ffffff" height="18px" width="18px" />,
    buttonClass: 'text-white  bg-swopa-secondary-light-blue cursor-wait',
    helpText: 'Der Zahlungslink wird gerade versendet. Bitte warten Sie.',
    disabled: true,
  },
  paymentLinkSent: {
    buttonContent: 'Zahlungslink gesendet',
    buttonClass: 'text-black bg-swopa-accent-green/50 cursor-help',
    helpText: 'Zahlungslink an Patient gesendet.',
    disabled: true,
  },
  generalError: {
    buttonContent: <Error height="18px" width="18px" />,
    buttonClass: 'text-white cursor-help bg-swopa-warning-red/50',
    helpText:
      'Der Zahlungslink konnte nicht gesendet werden. Versuchen Sie es erneut oder kontaktieren Sie den Administrator.',
    disabled: true,
  },
  prescriptionNotSent: {
    buttonContent: 'Zahlungslink senden',
    buttonClass: 'text-white  bg-swopa-secondary-light-blue cursor-help',
    helpText: 'Um den Zahlungslink zu versenden, muss zunächst ein Rezept hochgeladen werden.',
    disabled: true,
  },
};

function SendPaymentLinkButton({
  handleRefetchAppointments,
  appointmentId,
  wasPaymentLinkSent,
}: SendPaymentLinkButtonProps) {
  const [buttonState, setButtonState] = useState<State>(stateMap.idle);
  const { setError } = useContext(GlobalErrorContext);

  const { wasPrescriptionSent } = usePrescriptionContext();

  useEffect(() => {
    if (!wasPrescriptionSent) {
      setButtonState(stateMap.prescriptionNotSent);
      return;
    }

    if (!wasPaymentLinkSent) {
      setButtonState(stateMap.idle);
    } else {
      setButtonState(stateMap.paymentLinkSent);
    }
  }, [wasPaymentLinkSent, wasPrescriptionSent]);

  const handleGeneralError = () => {
    setButtonState(stateMap.generalError);
    setTimeout(() => setButtonState(stateMap.idle), 5000);
    setError('Etwas ist schiefgelaufen. Bitte versuchen Sie es erneut.');
  };

  const upload = () => {
    setButtonState(stateMap.loading);
    http
      .post('online-payments/unzer/prescription-paylink/', {
        appointment: appointmentId,
      })
      .then(() => {
        setButtonState(stateMap.paymentLinkSent);
        handleRefetchAppointments();
      })
      .catch(() => {
        handleGeneralError();
      });
  };

  return (
    <button
      type="button"
      title={buttonState.helpText}
      className={classNames(
        'w-full h-full p-3 rounded-[4px] font-radikal text-xs uppercase flex justify-center items-center disabled:opacity-50 hover:ease-in-out',
        buttonState.buttonClass,
      )}
      onClick={upload}
      disabled={buttonState.disabled}
    >
      {buttonState.buttonContent}
    </button>
  );
}

export default SendPaymentLinkButton;
